import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { ASSETS, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffSignup() {
  const { user, loading, bootstrapFromToken } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    position: "",
    phone: "",
    invite_code: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) {
    if (user.role === "staff") return <Navigate to="/staff" replace />;
    return <Navigate to="/admin" replace />;
  }

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/staff/signup`, {
        ...form,
        invite_code: form.invite_code.trim().toUpperCase(),
      });
      // Persist the returned token so the AuthContext picks it up on next render
      localStorage.setItem("trio_admin_token", data.access_token);
      localStorage.setItem("trio_admin_user", JSON.stringify(data.user));
      toast.success(`Welcome to the crew, ${data.user.name.split(" ")[0]}!`);
      if (typeof bootstrapFromToken === "function") {
        await bootstrapFromToken();
      }
      navigate("/staff");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create account");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--trio-bg)] text-black flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md bg-white border border-black/10 shadow-xl rounded-lg p-8">
        <div className="text-center mb-8">
          <img src={ASSETS.logo} alt="Trio" className="h-14 w-auto mx-auto mb-4" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            Join the Trio Crew
          </div>
          <div className="text-xs text-black/60 mt-1">
            Ask the owner for the current invite code.
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="staff-signup-form">
          <Field label="Full Name" testid="signup-name" required value={form.name} onChange={(v) => upd("name", v)} placeholder="First and last" />
          <Field label="Email" type="email" testid="signup-email" required value={form.email} onChange={(v) => upd("email", v)} placeholder="your@email.com" hint="Use your real email — we'll send password resets here." />
          <div>
            <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
              Password
            </label>
            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={form.password}
                onChange={(e) => upd("password", e.target.value)}
                data-testid="signup-password"
                className="w-full bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-3 pr-11 text-black focus:outline-none rounded"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                data-testid="signup-toggle-visibility"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black/70"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[10px] text-black/40 mt-1">
              At least 8 characters. No special format required.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Position" testid="signup-position" placeholder="Server, Bartender…" value={form.position} onChange={(v) => upd("position", v)} />
            <Field label="Phone" type="tel" testid="signup-phone" placeholder="Optional" value={form.phone} onChange={(v) => upd("phone", v)} />
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
              Invite Code
            </label>
            <input
              required
              value={form.invite_code}
              onChange={(e) => upd("invite_code", e.target.value.toUpperCase())}
              placeholder="ASK THE OWNER"
              data-testid="signup-invite-code"
              autoCapitalize="characters"
              className="w-full mt-1 bg-white border-2 border-[var(--trio-primary)]/50 focus:border-[var(--trio-primary)] px-4 py-3 text-black placeholder-black/30 focus:outline-none rounded font-mono tracking-widest text-center uppercase"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="signup-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-sm tracking-widest uppercase font-bold disabled:opacity-60 rounded mt-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create Staff Account
          </button>
        </form>

        <div className="mt-6 text-center space-y-1">
          <Link to="/staff/login" className="block text-[11px] tracking-widest uppercase text-black/60 hover:text-[var(--trio-primary)]">
            Already have an account? Sign in
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-black/40 hover:text-black/70">
            <ArrowLeft className="w-3 h-3" /> Back to Trio
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, hint, onChange, value, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
        {label}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-3 text-black placeholder-black/40 focus:outline-none rounded"
      />
      {hint && <div className="text-[10px] text-black/40 mt-1">{hint}</div>}
    </div>
  );
}
